/* eslint-disable no-console */
import {
  AnalyticsConfig,
  AnalyticsEvent,
  PageViewEvent,
  UserIdentity
} from '../types';
import { sanitizeUserProperties, shouldInitializeAnalytics } from '../utils';

declare global {
  interface Window {
    _hsq: any[];
  }
}

let hubSpotInitialized = false;

const getHsq = () => {
  if (typeof window !== 'undefined') {
    return (window._hsq = window._hsq || []);
  }
  return [];
};

export const initHubSpot = (
  config: AnalyticsConfig,
  user?: UserIdentity
): void => {
  if (hubSpotInitialized) {
    return;
  }

  const shouldInit =
    shouldInitializeAnalytics(user) && config.hubspot?.enabled !== false;

  if (shouldInit && config.hubspot?.hubspotId) {
    try {
      // Load HubSpot tracking script
      const script = document.createElement('script');
      script.type = 'text/javascript';
      script.id = 'hs-script-loader';
      script.async = true;
      script.defer = true;
      script.src = `//js.hs-scripts.com/${config.hubspot.hubspotId}.js`;
      document.head.appendChild(script);

      hubSpotInitialized = true;
    } catch (error) {
      console.warn('Failed to initialize HubSpot:', error);
    }
  }
};

export const identifyUser = (user: UserIdentity): void => {
  if (!hubSpotInitialized || !user.id) return;

  try {
    const properties = sanitizeUserProperties(user);
    const hsq = getHsq();
    hsq.push([
      'identify',
      {
        id: user.id,
        ...properties
      }
    ]);
  } catch (error) {
    console.warn('Failed to identify user in HubSpot:', error);
  }
};

export const trackEvent = (event: AnalyticsEvent): void => {
  if (!hubSpotInitialized) return;

  try {
    const hsq = getHsq();
    hsq.push(['trackEvent', event.name, event.properties]);
  } catch (error) {
    console.warn('Failed to track event in HubSpot:', error);
  }
};

export const trackPageView = (event: PageViewEvent): void => {
  if (!hubSpotInitialized) return;

  try {
    const path = event.path || window.location.pathname;
    const hsq = getHsq();
    hsq.push(['setPath', path]);
    hsq.push(['trackPageView']);
  } catch (error) {
    console.warn('Failed to track page view in HubSpot:', error);
  }
};

export const resetHubSpot = (): void => {
  if (hubSpotInitialized) {
    try {
      const hsq = getHsq();
      hsq.push(['reset']);
      hubSpotInitialized = false;
    } catch (error) {
      console.warn('Failed to reset HubSpot:', error);
    }
  }
};

export const getHubSpotInitialized = (): boolean =>
  hubSpotInitialized ||
  (typeof window !== 'undefined' && window._hsq && window._hsq.length > 0);
